import { useState, useRef, useEffect, useCallback } from 'react'
import { IconChevronDown } from './icons'

interface SelectOption {
  readonly value: string
  readonly label: string
}

interface CustomSelectProps {
  readonly id?: string
  readonly value: string
  readonly onChange: (value: string) => void
  readonly options: readonly SelectOption[]
  readonly placeholder?: string
  readonly disabled?: boolean
  readonly className?: string
}

export function CustomSelect({
  id,
  value,
  onChange,
  options,
  placeholder = '',
  disabled = false,
  className = '',
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedLabel = options.find((o) => o.value === value)?.label ?? placeholder

  const handleToggle = useCallback(() => {
    if (!disabled) setIsOpen((prev) => !prev)
  }, [disabled])

  const handleSelect = useCallback(
    (val: string) => {
      onChange(val)
      setIsOpen(false)
    },
    [onChange],
  )

  // Close on outside click / touch
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: Event) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('pointerdown', handler)
    return () => { document.removeEventListener('pointerdown', handler) }
  }, [isOpen])

  return (
    <div
      ref={containerRef}
      id={id}
      className={`relative ${className}`}
    >
      {/* Trigger button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className="flex w-full items-center justify-between rounded-xl border border-tg-hint/20 px-3 py-2.5 text-left text-sm outline-none transition-all duration-150 disabled:opacity-50 bg-tg-secondary hover:border-tg-hint/40 focus:border-[var(--tg-theme-button-color)] focus:shadow-sm"
        style={{
          color: value ? 'var(--tg-theme-text-color)' : 'var(--tg-theme-hint-color)',
        }}
      >
        <span className="min-w-0 flex-1 truncate">{selectedLabel}</span>
        <span className="ml-1 shrink-0 text-tg-hint transition-transform duration-200" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          <IconChevronDown size={14} />
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute left-0 right-0 z-50 mt-1 max-h-52 overflow-y-auto rounded-xl border border-tg-hint/20 shadow-lg bg-tg-secondary animate-slide-down"
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { handleSelect(opt.value) }}
              className="flex w-full items-center px-3 py-2 text-left text-sm transition-all duration-150 active:opacity-70 active:scale-[0.99] hover:bg-tg-bg"
              style={{
                color: opt.value === value
                  ? 'var(--tg-theme-button-color)'
                  : 'var(--tg-theme-text-color)',
                backgroundColor: opt.value === value
                  ? 'color-mix(in srgb, var(--tg-theme-button-color) 15%, transparent)'
                  : 'transparent',
              }}
            >
              <span className="min-w-0 flex-1 truncate">{opt.label}</span>
            </button>
          ))}
          {options.length === 0 && (
            <div
              className="px-3 py-2 text-center text-xs text-tg-hint"
            >
              —
            </div>
          )}
        </div>
      )}
    </div>
  )
}
